import { useAuth } from 'react-oidc-context';
import { useQueryEffectivePermissions } from '@/modules/shared/query-client';
import { RolePermission, effectiveRolesInCompany } from '@/modules/shared/types/account';

/**
 * Provides a permission checking function for the current authenticated user.
 *
 * Usage:
 * const can = usePermissionChecker(companyId);
 * if (can('canEditUser')) { ... }
 *
 * Backed by the server's effective-permission projection (GET
 * /config-api/user/effective-permissions) — the same source getRolesInCompany uses. Previously
 * this reconstructed capabilities from CompanyRole names, which returned nothing for a
 * service-account member/owner (they hold no CompanyRole) and so denied every ctx.can()-gated
 * page for them; it also only approximated the group/tenant cascade the server already computes.
 */
export function usePermissionChecker(companyId?: string) {
  const auth = useAuth();
  const sub = auth.isAuthenticated ? auth.user?.profile.sub : '';
  const { data: effectivePermissions } = useQueryEffectivePermissions({ userUniqueId: sub || '' });

  if (!auth.isAuthenticated || !companyId || !effectivePermissions) {
    return () => false;
  }

  // Single pre-OR-ed row for the company ([] when the user has no access to it).
  const roles = effectiveRolesInCompany(effectivePermissions, companyId);

  return (permission: keyof RolePermission): boolean =>
    roles.some((r) => Boolean(r[permission]));
}
